import { useCallback, useEffect, useState } from 'react';
import {
  useJwt, useAuthHeaders, FEEDBACK_KIND_LABELS, FEEDBACK_STATUS_LABELS, FEEDBACK_STATUSES,
  type FeedbackKind, type FeedbackStatus, type FeedbackContext,
} from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { Badge, Button, Modal, Select, Tabs } from '../ds/index.js';
import { GeneralChat } from '../overlays/GeneralChat.js';
import { SIDE_PANEL_WIDTH } from '../people/SidePanel.js';

/** GET /admin/feedback — voir FeedbackService (apps/api). Aucun champ Trello : outil interne. */
interface FeedbackRow {
  id: string;
  kind: FeedbackKind;
  status: FeedbackStatus;
  message: string;
  authorName: string;
  authorRole: string;
  authorUserId: string | null;
  context: FeedbackContext;
  hasScreenshot: boolean;
  createdAt: string;
}

type KindFilter = 'all' | FeedbackKind;

const STATUS_TONE: Record<FeedbackStatus, 'primary' | 'neutral' | 'success' | 'warning'> = {
  new: 'primary', acknowledged: 'warning', resolved: 'success', dismissed: 'neutral',
};
const ROLE_LABELS: Record<string, string> = { OWNER: 'Propriétaire', ADMIN: 'Admin', MEMBER: 'Membre', GUEST: 'Invité' };

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const excerpt = (text: string) => (text.length > 90 ? `${text.slice(0, 89)}…` : text);

/**
 * AdminFeedback — Admin › Retours (OWNER seulement : menu et API).
 * Problèmes et idées envoyés depuis le campus ; trier par statut.
 * La messagerie s'ouvre à droite sans quitter la page (bouton « Messagerie »,
 * ou « Répondre en message privé » : conversation avec l'auteur ; un OWNER
 * écrit à tout membre, sans demande de connexion).
 */
export function AdminFeedback() {
  const jwt = useJwt();
  const headers = useAuthHeaders(getTenantSlug());
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<'open' | 'all' | FeedbackStatus>('open');
  const [selected, setSelected] = useState<FeedbackRow | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [dmUserId, setDmUserId] = useState<string | null>(null);
  const replyByDm = (userId: string) => { setSelected(null); setDmUserId(userId); setChatOpen(true); };

  const load = useCallback(() => {
    if (!jwt) return;
    setLoading(true);
    const query = new URLSearchParams();
    if (kind !== 'all') query.set('kind', kind);
    if (status !== 'all' && status !== 'open') query.set('status', status);
    fetch(`${API_BASE}/admin/feedback?${query}`, { headers })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: FeedbackRow[]) => setRows(data))
      .catch(() => setError('Impossible de charger les retours.'))
      .finally(() => setLoading(false));
  }, [jwt, kind, status]);

  useEffect(() => { load(); }, [load]);

  // « À traiter » = nouveaux et pris en compte (filtré ici : l'API filtre un seul statut).
  const visible = status === 'open' ? rows.filter((r) => r.status === 'new' || r.status === 'acknowledged') : rows;
  const n = visible.length;

  const onUpdated = (updated: FeedbackRow) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginRight: chatOpen ? SIDE_PANEL_WIDTH - 24 : 0 }}>
      <header className="pl-page-head">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 className="pl-page-title">Retours</h1>
          <p className="pl-page-sub">
            Problèmes signalés et idées proposées depuis le campus (menu de l&apos;avatar › « Signaler un problème ou proposer une idée »).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon="message" aria-pressed={chatOpen} onClick={() => setChatOpen((v) => !v)}>Messagerie</Button>
          <Button variant="secondary" icon="refresh" onClick={load} disabled={loading}>Actualiser</Button>
        </div>
      </header>

      {error && (
        <div className="pl-alert" role="alert" style={{ justifyContent: 'space-between' }}>
          <span>{error}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setError(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Tabs<KindFilter>
          aria-label="Type de retour"
          items={[{ id: 'all', label: 'Tous' }, { id: 'bug', label: 'Problèmes' }, { id: 'idea', label: 'Idées' }]}
          value={kind}
          onChange={setKind}
        />
        <div style={{ width: 200 }}>
          <Select
            aria-label="Statut"
            size="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            options={[
              { value: 'open', label: 'À traiter' },
              { value: 'all', label: 'Tous les statuts' },
              ...FEEDBACK_STATUSES.map((s) => ({ value: s, label: FEEDBACK_STATUS_LABELS[s] })),
            ]}
          />
        </div>
        <span className="pl-page-sub" style={{ margin: 0 }}>{loading ? 'Chargement…' : `${n} retour${n > 1 ? 's' : ''}`}</span>
      </div>

      {!loading && (
        <div className="pl-panel" style={{ overflow: 'hidden' }}>
          <table className="pl-table">
            <thead><tr><th>Reçu le</th><th>Type</th><th>Message</th><th>Auteur</th><th>Carte</th><th>Statut</th></tr></thead>
            <tbody>
              {n === 0 && (
                <tr><td colSpan={6} className="pl-muted" style={{ textAlign: 'center', padding: 24 }}>
                  Aucun retour {status === 'open' ? 'à traiter' : 'pour ces filtres'}.
                </td></tr>
              )}
              {visible.map((r) => (
                <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                  <td className="pl-muted pl-num" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                  <td><Badge tone={r.kind === 'bug' ? 'danger' : 'primary'}>{FEEDBACK_KIND_LABELS[r.kind]}</Badge></td>
                  <td>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setSelected(r); }} style={{ all: 'unset', cursor: 'pointer' }}>
                      {excerpt(r.message)}
                    </button>
                  </td>
                  <td>{r.authorName} <span className="pl-muted">· {ROLE_LABELS[r.authorRole] ?? r.authorRole}</span></td>
                  <td className="pl-muted">{r.context.mapName ?? r.context.map ?? '-'}</td>
                  <td><Badge tone={STATUS_TONE[r.status]}>{FEEDBACK_STATUS_LABELS[r.status]}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <FeedbackDetail row={selected} onClose={() => setSelected(null)} onUpdated={onUpdated} onReply={replyByDm} />}

      {chatOpen && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: SIDE_PANEL_WIDTH, zIndex: 'var(--z-panel)' as unknown as number }}>
          <GeneralChat onClose={() => setChatOpen(false)} dmUserId={dmUserId} onDmOpened={() => setDmUserId(null)} />
        </div>
      )}
    </div>
  );
}

function FeedbackDetail({ row, onClose, onUpdated, onReply }: {
  row: FeedbackRow;
  onClose: () => void;
  onUpdated: (r: FeedbackRow) => void;
  onReply: (userId: string) => void;
}) {
  const headers = useAuthHeaders(getTenantSlug());
  const [image, setImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture servie par l'API (JWT + en-tête tenant) : récupérée en blob, comme les enregistrements.
  useEffect(() => {
    if (!row.hasScreenshot) return;
    let url: string | null = null;
    let cancelled = false;
    fetch(`${API_BASE}/admin/feedback/${encodeURIComponent(row.id)}/screenshot`, { headers })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => { if (cancelled) return; url = URL.createObjectURL(blob); setImage(url); })
      .catch(() => { if (!cancelled) setError('La capture n’a pas pu être chargée.'); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [row.id, row.hasScreenshot]);

  async function setStatus(status: FeedbackStatus) {
    if (status === row.status) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/feedback/${encodeURIComponent(row.id)}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onUpdated(await res.json() as FeedbackRow);
    } catch {
      setError('Le statut n’a pas pu être changé.');
    } finally {
      setSaving(false);
    }
  }

  const c = row.context;
  const where = [c.mapName ?? c.map, c.tileX !== undefined && c.tileY !== undefined ? `case ${c.tileX}, ${c.tileY}` : null, c.bubble ? `bulle ${c.bubble}` : null]
    .filter(Boolean).join(' · ');
  const details: Array<[string, string | undefined]> = [
    ['Auteur', `${row.authorName} (${(ROLE_LABELS[row.authorRole] ?? row.authorRole).toLowerCase()})`],
    ['Reçu le', formatDate(row.createdAt)],
    ['Où', where || undefined],
    ['Navigateur', c.userAgent],
    ['Écran', [c.screen, c.viewport && `fenêtre ${c.viewport}`].filter(Boolean).join(' · ') || undefined],
    ['Version', c.appVersion],
  ];

  return (
    <Modal
      title={FEEDBACK_KIND_LABELS[row.kind]}
      size="lg"
      onClose={onClose}
      footer={
        <>
          {row.authorUserId && (
            <Button variant="primary" icon="message" onClick={() => onReply(row.authorUserId!)}>Répondre en message privé</Button>
          )}
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--color-text-primary)' }}>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{row.message}</p>

        <div style={{ width: 240 }}>
          <Select
            label="Statut"
            value={row.status}
            disabled={saving}
            onChange={(e) => { void setStatus(e.target.value as FeedbackStatus); }}
            options={FEEDBACK_STATUSES.map((s) => ({ value: s, label: FEEDBACK_STATUS_LABELS[s] }))}
          />
        </div>
        {error && <div className="pl-alert" role="alert">{error}</div>}

        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', fontSize: 13 }}>
          {details.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt className="pl-muted">{k}</dt>
              <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
            </div>
          ))}
        </dl>

        {row.hasScreenshot && (
          image
            ? <img src={image} alt="Capture du campus jointe au retour" style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)' }} />
            : !error && <p className="pl-help" style={{ margin: 0 }}>Chargement de la capture…</p>
        )}
      </div>
    </Modal>
  );
}
